import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Twitter, Linkedin, Facebook, Instagram } from "lucide-react";

const DEFAULTS = {
  logo_text: "Simple Invoice",
  description: "Create professional invoices in seconds. Fast, simple and completely free.",
  copyright: `© ${new Date().getFullYear()} Simple Invoice. All rights reserved.`,
  quick_links: [
    { label: "Home", url: "/" },
    { label: "Create Invoice", url: "/create" },
    { label: "About", url: "/about" },
    { label: "FAQ", url: "/faq" },
    { label: "Contact", url: "/contact" },
  ],
  legal_links: [
    { label: "Privacy Policy", url: "/privacy" },
    { label: "Terms & Conditions", url: "/terms" },
    { label: "Sitemap", url: "/sitemap" },
  ],
};

export default function Footer() {
  const [footer, setFooter] = useState(DEFAULTS);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/site-content`)
      .then((r) => {
        const f = r.data?.footer;
        if (f) setFooter({ ...DEFAULTS, ...f });
      })
      .catch(() => {});
  }, []);

  const NavLink = ({ url, children }) =>
    /^https?:\/\//.test(url) ? (
      <a href={url} target="_blank" rel="noreferrer" className="hover:text-white transition-colors">{children}</a>
    ) : (
      <Link to={url} className="hover:text-white transition-colors">{children}</Link>
    );

  return (
    <footer className="mt-24 bg-blue-600 text-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div>
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo192.png" alt={footer.logo_text} className="w-9 h-9 rounded-xl" />
            <span className="font-heading font-bold text-lg">{footer.logo_text}</span>
          </Link>
          <p className="mt-4 text-sm text-blue-100 leading-relaxed">{footer.description}</p>
        </div>

        <div>
          <div className="font-heading font-semibold text-sm mb-4">Quick Links</div>
          <ul className="space-y-2 text-sm text-blue-100" data-testid="footer-quick-links">
            {(footer.quick_links || []).map((l, i) => (
              <li key={i}><NavLink url={l.url}>{l.label}</NavLink></li>
            ))}
          </ul>
        </div>

        <div>
          <div className="font-heading font-semibold text-sm mb-4">Legal</div>
          <ul className="space-y-2 text-sm text-blue-100" data-testid="footer-legal-links">
            {(footer.legal_links || []).map((l, i) => (
              <li key={i}><NavLink url={l.url}>{l.label}</NavLink></li>
            ))}
          </ul>
        </div>

        <div>
          <div className="font-heading font-semibold text-sm mb-4">Connect</div>
          <div className="flex gap-3">
            <a href="https://facebook.com" target="_blank" rel="noreferrer" aria-label="Facebook" data-testid="social-facebook" className="w-11 h-11 rounded-full bg-white/15 grid place-items-center hover:bg-white/25 transition-colors"><Facebook size={16} /></a>
            <a href="https://twitter.com" target="_blank" rel="noreferrer" aria-label="Twitter" data-testid="social-twitter" className="w-11 h-11 rounded-full bg-white/15 grid place-items-center hover:bg-white/25 transition-colors"><Twitter size={16} /></a>
            <a href="https://linkedin.com" target="_blank" rel="noreferrer" aria-label="LinkedIn" data-testid="social-linkedin" className="w-11 h-11 rounded-full bg-white/15 grid place-items-center hover:bg-white/25 transition-colors"><Linkedin size={16} /></a>
            <a href="https://instagram.com" target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="social-instagram" className="w-11 h-11 rounded-full bg-white/15 grid place-items-center hover:bg-white/25 transition-colors"><Instagram size={16} /></a>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-5 text-xs text-blue-100 text-center">
          {footer.copyright}
        </div>
      </div>
    </footer>
  );
}
